type BackendError =
  | { detail?: string | Array<{ msg?: string }>; error?: string; message?: string }
  | any;

export class ApiResponseError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiResponseError";
  }
}

function isHtmlResponseText(text: string): boolean {
  return /^\s*<!doctype html/i.test(text) || /^\s*<html[\s>]/i.test(text);
}

function isPythonTracebackText(text: string): boolean {
  return /^\s*traceback \(most recent call last\):/i.test(text);
}

export function parseBackendErrorText(text: string): string {
  const raw = (text || "").trim();
  if (!raw) return "Request failed";

  try {
    const parsed = JSON.parse(raw) as BackendError;
    const detail = (parsed as any)?.detail;
    if (typeof detail === "string" && detail.trim()) return detail.trim();
    if (Array.isArray(detail)) {
      const first = detail.find((d) => d?.msg)?.msg;
      if (typeof first === "string" && first.trim()) return first.trim();
    }
    const message = (parsed as any)?.message;
    if (typeof message === "string" && message.trim()) return message.trim();
    const error = (parsed as any)?.error;
    if (typeof error === "string" && error.trim()) return error.trim();
  } catch {
    // non-JSON response
  }

  if (isHtmlResponseText(raw) || isPythonTracebackText(raw)) {
    return "Server error. Please try again.";
  }

  return raw;
}

export function humanizeAuthError(message: string): string {
  const m = (message || "").toLowerCase();
  if (m.includes("current password is incorrect")) return "Invalid password";
  if (m.includes("invalid code")) return "Invalid code";
  if (m.includes("challenge expired")) return "Code expired";
  if (m.includes("challenge not found")) return "Code expired";
  if (m.includes("challenge already used")) return "Code already used";
  if (m.includes("too many attempts")) return "Too many attempts";
  return message || "Request failed";
}

export type SecurityCodeIssue = "invalid" | "expired" | "used" | "attempts";

export function getSecurityCodeIssue(message?: string | null): SecurityCodeIssue | null {
  const msg = (message || "").toLowerCase();
  if (!msg) return null;
  if (msg.includes("invalid code")) return "invalid";
  if (msg.includes("code expired")) return "expired";
  if (msg.includes("code already used")) return "used";
  if (msg.includes("too many attempts")) return "attempts";
  return null;
}

export async function errorMessageFromResponse(
  res: Response,
  fallback = "Request failed",
  options?: { humanizeAuth?: boolean }
): Promise<string> {
  let text = "";
  try {
    text = await res.text();
  } catch {
    // ignore
  }
  const parsed = text && !isHtmlResponseText(text) && !isPythonTracebackText(text)
    ? parseBackendErrorText(text)
    : fallback;
  return options?.humanizeAuth ? humanizeAuthError(parsed) : parsed;
}

export async function jsonFromResponse<T>(
  res: Response,
  fallback = "Unexpected server response. Please try again."
): Promise<T> {
  if (res.status === 204 || res.status === 205) {
    return null as T;
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.toLowerCase().includes("application/json")) {
    throw new Error(fallback);
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw new Error(fallback);
  }
}
